/**
 * A árvore de rotas.
 *
 * Duas camadas, e a divisão é de segurança, não de organização: **`/entrar` fica
 * FORA do shell**. A tela de login não tem sidebar, nem topbar, nem seletor de
 * entidade — e não pode montar nada que suponha uma sessão. Aninhá-la sob o shell
 * faria a coluna do cartão nascer com um cabeçalho que só existe depois de entrar.
 *
 * Todo caminho de `caminhos.ts` tem tela própria: o mapa abaixo é total, e o estado "em
 * construção" que cobria as áreas ainda sem tela saiu com a última delas (F-05).
 */
import type { FunctionComponent } from 'react'
import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
} from '@tanstack/react-router'
import { sessaoAtual } from '../api/recursos.ts'
import { Shell } from '../shell/shell.tsx'
import { ProvedorDeToasts } from '../componentes/toast.tsx'
import { Entrar } from './entrar.tsx'
import { CAMINHOS, CHAVES_DE_BUSCA, type Caminho } from './caminhos.ts'
import { entradaComRetorno } from './entrada-com-retorno.ts'

type BuscaDeRota = Partial<Record<(typeof CHAVES_DE_BUSCA)[number], string>>

/**
 * As telas que já existem — cada uma no SEU pedaço, carregada quando a rota abre
 * (lote U5 · achado PERF-01).
 *
 * Antes, `raiz.tsx` importava as 34 telas estaticamente, e o resultado era um pacote só
 * de 1,4 MB: quem abre o Dashboard baixava a conciliação bancária, a DRE e a folha de
 * RH junto — e pagava por isso antes de ver o primeiro número. `lazyRouteComponent`
 * troca o import estático por um `import()`, e o empacotador faz o resto.
 *
 * O nome da exportação vai junto porque estas telas não usam `export default`: é a mesma
 * decisão de sempre neste repositório — exportação nomeada não some num rename.
 */
const COMPONENTE_POR_CAMINHO: Record<Caminho, FunctionComponent> = {
  '/': lazyRouteComponent(() => import('./dashboard.tsx'), 'Dashboard'),
  '/configuracoes': lazyRouteComponent(() => import('./pagina-configuracoes.tsx'), 'PaginaDeConfiguracoes'),
  '/configuracoes/entidades': lazyRouteComponent(() => import('./configuracoes/entidades.tsx'), 'Entidades'),
  '/configuracoes/permissoes': lazyRouteComponent(() => import('./configuracoes/permissoes.tsx'), 'Permissoes'),
  '/perfil': lazyRouteComponent(() => import('./pagina-perfil.tsx'), 'PaginaDePerfil'),
  '/documentacao': lazyRouteComponent(() => import('./documentacao.tsx'), 'Documentacao'),
  '/empresas': lazyRouteComponent(() => import('./empresas-rota.tsx'), 'RotaDeEmpresas'),
  '/contatos': lazyRouteComponent(() => import('./contatos.tsx'), 'Contatos'),
  '/negocios': lazyRouteComponent(() => import('./negocios-rota.tsx'), 'RotaDeNegocios'),
  '/pedidos': lazyRouteComponent(() => import('./pedidos-rota.tsx'), 'RotaDePedidos'),
  '/metas': lazyRouteComponent(() => import('./metas.tsx'), 'Metas'),
  '/faturamento': lazyRouteComponent(() => import('./faturamento.tsx'), 'Faturamento'),
  '/recebiveis': lazyRouteComponent(() => import('./recebiveis.tsx'), 'Recebiveis'),
  '/contas-bancarias': lazyRouteComponent(() => import('./tesouraria.tsx'), 'Tesouraria'),
  '/fluxo-de-caixa': lazyRouteComponent(() => import('./fluxo-de-caixa.tsx'), 'FluxoDeCaixa'),
  '/conciliacao': lazyRouteComponent(() => import('./conciliacao.tsx'), 'Conciliacao'),
  '/contas-a-pagar': lazyRouteComponent(() => import('./contas-a-pagar.tsx'), 'ContasAPagar'),
  // DS-615: a Central de Tributos (apuração e guia); não está nas 49 telas, nasce do pedido de 01/10.
  '/tributos': lazyRouteComponent(() => import('./tributos.tsx'), 'Tributos'),
  '/aprovacoes': lazyRouteComponent(() => import('./aprovacoes.tsx'), 'Aprovacoes'),
  '/comissoes': lazyRouteComponent(() => import('./comissoes.tsx'), 'Comissoes'),
  '/configuracoes/regras-comissao': lazyRouteComponent(() => import('./regras-comissao.tsx'), 'RegrasDeComissao'),
  '/previsao': lazyRouteComponent(() => import('./previsao.tsx'), 'Previsao'),
  '/relatorios': lazyRouteComponent(() => import('./relatorios.tsx'), 'Relatorios'),
  '/plano-de-contas': lazyRouteComponent(() => import('./plano-de-contas.tsx'), 'PlanoDeContas'),
  '/ativos-intangiveis': lazyRouteComponent(() => import('./ativos-intangiveis.tsx'), 'AtivosIntangiveis'),
  '/orcamento': lazyRouteComponent(() => import('./orcamento.tsx'), 'Orcamento'),
  '/variancia': lazyRouteComponent(() => import('./variancia.tsx'), 'Variancia'),
  '/rentabilidade': lazyRouteComponent(() => import('./rentabilidade.tsx'), 'Rentabilidade'),
  '/dre': lazyRouteComponent(() => import('./dre.tsx'), 'Dre'),
  '/fornecedores': lazyRouteComponent(() => import('./fornecedores.tsx'), 'Fornecedores'),
  '/campanhas': lazyRouteComponent(() => import('./campanhas.tsx'), 'Campanhas'),
  '/busca': lazyRouteComponent(() => import('./busca-global.tsx'), 'BuscaGlobal'),
  '/inbox': lazyRouteComponent(() => import('./inbox.tsx'), 'Inbox'),
  '/rh': lazyRouteComponent(() => import('./rh.tsx'), 'Rh'),
  '/tarefas': lazyRouteComponent(() => import('./tarefas.tsx'), 'Tarefas'),
  '/tarefas/semana': lazyRouteComponent(() => import('./tarefas.tsx'), 'AgendaDeTarefas'),
}

/**
 * A raiz não desenha nada — e a pilha de toasts DESCEU dela.
 *
 * Ela morava aqui para sobreviver à navegação que o ato provocou. Só que a preferência
 * de idioma e de data vive no shell, abaixo, e o conteúdo de um toast é renderizado onde
 * o provedor está: tudo que entrava num aviso lia o PADRÃO — português, `DD/MM/AAAA` —
 * sem erro nenhum, porque é o mesmo valor que quase todo mundo tem. O botão "Fechar" do
 * aviso dizia "Fechar" com a interface em inglês, ao lado do "Close" do diálogo.
 *
 * Cada mundo passa a ter a sua pilha, e o motivo original está preservado: `comShell` é
 * rota de LAYOUT, então navegar entre telas não a desmonta e o toast continua atravessando
 * a navegação exatamente como antes. O que se perde é a travessia entre o app e o login —
 * e um aviso do app deitando sobre a tela de entrada não era feição, era efeito colateral.
 *
 * `/entrar` ganha a sua porque também usa `useToast` (entrar.tsx:92) para dizer que a
 * entrada falhou. Sem provedor, `useToast` devolve uma função muda de propósito
 * (toast.tsx:67-70): a tela perderia o aviso em silêncio.
 */
const raiz = createRootRoute({ component: () => <Outlet /> })

const entrar = createRoute({
  getParentRoute: () => raiz,
  path: '/entrar',
  component: () => (
    <ProvedorDeToasts>
      <Entrar />
    </ProvedorDeToasts>
  ),
})

/**
 * Tudo que exige sessão vive sob o shell — e é ESTE `beforeLoad` que cobra a sessão.
 *
 * Sem ele, o comentário acima era só intenção: `digitalview.cc` abria o shell inteiro
 * para quem nunca entrou, com sidebar, topbar e um nome de usuário fixo no rodapé.
 * Nenhum dado vazava (a API recusa tudo com 401), mas a tela AFIRMAVA uma identidade
 * que não existia — e o defeito foi encontrado pelo fundador, em produção, no dia do
 * primeiro deploy. O portão existia no servidor; o cliente é que nunca o consultava.
 *
 * Falha fechada de propósito: se a pergunta não pode ser respondida — rede fora,
 * servidor mudo —, a resposta é `/entrar`. Montar o shell sob dúvida é o erro que
 * este bloco existe para não repetir.
 *
 * `beforeLoad` roda a cada navegação, e isso é feição, não desperdício: sessão que
 * expira no meio do uso derruba a pessoa na próxima tela em vez de deixá-la clicando
 * numa interface que só devolve erro.
 */
const comShell = createRoute({
  getParentRoute: () => raiz,
  id: 'shell',
  beforeLoad: async ({ location }) => {
    let email: string | null = null
    try {
      ;({ email } = await sessaoAtual())
    } catch {
      email = null
    }
    /*
      O destino vai junto (DS-638): o link do aviso abre `/aprovacoes` e, depois do segundo
      fator, a pessoa volta para lá. Por `href`, e não por `search`, para o `de` sair como
      texto codificado e não como JSON do roteador. Quem confere a origem é `/entrar`.
    */
    if (email === null) throw redirect({ href: entradaComRetorno(location.href) })
  },
  component: Shell,
})

const rotas = CAMINHOS.map((caminho) => {
  const Componente = COMPONENTE_POR_CAMINHO[caminho]
  return createRoute({
    getParentRoute: () => comShell,
    path: caminho,
    /* O mapa é TOTAL (F-05): todo caminho tem tela, e o caminho novo sem tela é erro de tipo, não um
       "em construção" em produção. */
    component: Componente,
    /**
     * Os parâmetros de busca que o roteador conhece — e conhecer é o que impede o
     * TanStack de DESCARTÁ-LOS na navegação.
     *
     * `cat` é da 10.1; `id`, `novo` e `editar` são dos deep-links do cadastro;
     * `view` e `estagio` são os da 3.5 (`?view=lista`, `?estagio=<uuid>`); `from` é o
     * da 4.3 (`?from=<uuid do negócio ganho>`); `eixo` é o da 3.8 e `exec`/`setor` são os
     * pré-filtros da 3.9 — em todos, valor fora do enum é IGNORADO pela tela, e não
     * vira estado de erro (spec 05 §11). `relatorio` e `dom` são os da 8.7, e as
     * duas classes da spec 05 §11 convivem numa tela só: `?dom=` inválido é
     * ignorado (recorte), `?relatorio=` inválido cai em "não encontrado" (define
     * o escopo da tela). `situacao` é o recorte de 4.1 que os cards de assinatura
     * da 8.7 emitem — sem ele declarado aqui, o roteador o DESCARTA no clique.
     * A lista é fechada de propósito: parâmetro que não está aqui não sobrevive a um
     * clique, e é melhor que ele suma cedo, no desenvolvimento, do que numa tela que
     * o usuário abriu por link.
     *
     * **Tudo vira texto com `String()`**, e isso não é preguiça: o TanStack faz parse
     * JSON dos valores, então `?novo=1` chega como o NÚMERO 1 e `?novo="1"` como a
     * string. Comparar com `typeof === 'string'` descartava o primeiro — e o primeiro
     * é justamente a forma que um humano digita e que as specs de tela declaram nos
     * deep-links. Normalizar aqui é o que faz `?id=<uuid>` e `?novo=1` valerem igual.
     */
    validateSearch: (busca: Record<string, unknown>): BuscaDeRota => {
      const saida: BuscaDeRota = {}
      for (const chave of CHAVES_DE_BUSCA) {
        const valor = busca[chave]
        // Só primitivo vira texto. Um objeto viraria "[object Object]" e passaria
        // adiante como se fosse um id — que é o tipo de lixo que a validação existe
        // para barrar, não para converter.
        if (typeof valor === 'string') saida[chave] = valor
        else if (typeof valor === 'number' || typeof valor === 'boolean') saida[chave] = String(valor)
      }
      return saida
    },
  })
})

export const router = createRouter({
  routeTree: raiz.addChildren([entrar, comShell.addChildren(rotas)]),
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
